import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Expand, LibraryBig, LoaderCircle, Minimize2, Plus, Send, SquarePen, X } from 'lucide-react';
import toast from 'react-hot-toast';

import LoadingDots from '../../chat/components/LoadingDots';
import chatApi from '../../../api/chatApi';
import ledgerAgentApi, { LedgerAgentAction, LedgerAgentCandidate, LedgerAgentCandidateEvidence, LedgerAgentCheckpoint, LedgerAgentJob, LedgerAgentJobEvent, LedgerAgentOpenNormalizationEditorPayload, LedgerAgentSession } from '../../../api/ledgerAgentApi';
import StandardAccountSelector from './StandardAccountSelector';
import { StandardAccount } from '../../../api/types';

type DisplayMessage = {
    id: string;
    role: 'human' | 'ai';
    content: string;
    meta?: Record<string, unknown>;
    activity?: {
        type: string;
        message: string;
        status: 'active' | 'done';
    }[];
    suggested_questions?: string[];
};

type LedgerDiagnosisSummary = {
    issue_type?: string;
    focus_type?: string;
    what_i_checked?: string[];
    observed_problem?: string;
    recommended_action?: string;
    recommended_vendor_name?: string;
    recommended_normalized_description?: string;
    requires_confirmation?: boolean;
    confidence?: number;
};

type ResultPreviewRow = {
    doc_id?: string;
    transaction_id?: string;
    date?: string;
    vendor_name?: string;
    normalized_description?: string;
    amount_abs?: number;
    resolution_status?: string;
    finz_account_name?: string;
    fiscal_bucket?: string;
};

type ResultFilterChip = {
    label: string;
    value: string;
};

interface Props {
    businessId?: string;
    onClose?: () => void;
    onOpenTransaction?: (documentId: string) => void;
    onApplyResultSet?: (payload: { filters?: ResultFilterChip[]; strictVendorMatch?: boolean }) => void;
    onRefreshLedger?: () => void;
    onOpenNormalizationEditor?: (payload: LedgerAgentOpenNormalizationEditorPayload) => void;
}

const STORAGE_KEYS = {
    mode: 'ledger_chat_mode',
    agentModeActive: 'ledger_agent_mode_active',
    sessionId: 'ledger_agent_session_id',
    generalConversationId: 'ledger_general_conversation_id',
    generalMessages: 'ledger_general_chat_messages',
    panelMessages: 'ledger_panel_messages',
} as const;

const scopedStorageKey = (key: string, businessId?: string) => {
    const normalized = String(businessId || '').trim();
    return normalized ? `${key}:${normalized}` : key;
};

const readStoredId = (key: string): string | null => {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const normalized = raw.trim().toLowerCase();
    if (!normalized || normalized === 'null' || normalized === 'undefined') return null;
    return raw;
};

const shouldPollLedgerAgentSession = (payload: unknown): boolean => {
    if (!payload || typeof payload !== 'object') return false;
    const record = payload as Record<string, unknown>;
    const checkpoint = record.checkpoint;
    if (checkpoint && typeof checkpoint === 'object') return true;
    const session = record.session && typeof record.session === 'object'
        ? record.session as Record<string, unknown>
        : null;
    const activeJobId = String(session?.active_job_id || '').trim();
    const jobs = Array.isArray(record.jobs) ? record.jobs : [];
    if (!activeJobId || jobs.length === 0) return false;
    const activeJob = jobs.find((job) => {
        if (!job || typeof job !== 'object') return false;
        return String((job as Record<string, unknown>)._id || '').trim() === activeJobId;
    }) as Record<string, unknown> | undefined;
    if (!activeJob) return false;
    const status = String(activeJob.status || '').trim().toLowerCase();
    return ['queued', 'running'].includes(status);
};

const isGuidedRemediationJob = (job?: LedgerAgentJob | null): boolean => {
    const result = (job?.result || {}) as Record<string, unknown>;
    return result.mode === 'guided_remediation';
};

const isTransientAgentJob = (job?: LedgerAgentJob | null): boolean => Boolean(job?.spec && (job.spec as Record<string, unknown>).transient);

const isCandidateActionType = (actionType: string): boolean => (
    [
        'candidate_verify',
        'candidate_awaiting_approval',
        'candidate_keep_needs_review',
        'confirm_top_candidate',
        'use_candidate',
        'show_alternates',
    ].includes(actionType)
);

const buildStructuredStandardAccountMessage = (payload?: Record<string, unknown>): string => {
    const accountId = String(payload?.standard_account_id || '').trim();
    const fiscalBucket = String(payload?.fiscal_bucket || '').trim();
    const accountName = String(payload?.account_name || '').trim();
    const accountNumber = String(payload?.account_number || '').trim();
    if (!accountId) return String(payload?.message || '').trim();
    return `__structured_standard_account__:${accountId}|${fiscalBucket}|${accountName}|${accountNumber}`;
};

const formatStructuredStandardAccountMessage = (message: string): string => {
    const text = String(message || '').trim();
    const prefix = '__structured_standard_account__:';
    if (!text.startsWith(prefix)) return text;
    const payload = text.slice(prefix.length);
    const parts = payload.split('|');
    const fiscalBucket = String(parts[1] || '').trim();
    const accountName = String(parts[2] || '').trim();
    const accountNumber = String(parts[3] || '').trim();
    const label = [accountName, accountNumber].filter(Boolean).join(' | ');
    if (label) return label;
    return [fiscalBucket, accountName].filter(Boolean).join(' / ') || 'Selected standard account';
};

const isGenericWorkItemLabel = (value?: string): boolean => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return true;
    return normalized === 'unknown'
        || normalized === 'unknown vendor'
        || normalized === 'memo'
        || normalized === 'check'
        || normalized.startsWith('check ')
        || normalized.startsWith('check #')
        || normalized === 'dda regular deposit';
};

const buildWorkItemResultSet = (workItem?: Record<string, unknown> | null): { filters: ResultFilterChip[]; strictVendorMatch?: boolean } | null => {
    if (!workItem) return null;
    const vendorName = String(workItem.vendor_name || '').trim();
    const normalizedDescription = String(workItem.normalized_description || '').trim();
    const direction = String(workItem.direction || '').trim().toLowerCase();
    const hasSpecificVendor = vendorName && !isGenericWorkItemLabel(vendorName);
    const hasSpecificNormalizedDescription = normalizedDescription && !isGenericWorkItemLabel(normalizedDescription);

    const filters: ResultFilterChip[] = [{ label: 'Status', value: 'needs_review' }];

    if (direction === 'inflow' || direction === 'outflow') {
        filters.push({ label: 'Direction', value: direction });
    }

    if (hasSpecificNormalizedDescription) {
        filters.unshift({ label: 'Normalized Description', value: normalizedDescription });
    }

    if (hasSpecificVendor) {
        filters.unshift({ label: 'Vendor', value: vendorName });
        return { filters, strictVendorMatch: true };
    }

    if (hasSpecificNormalizedDescription) {
        return { filters };
    }

    if (normalizedDescription) {
        filters.unshift({ label: 'Search', value: normalizedDescription });
        return { filters };
    }

    if (vendorName) {
        filters.unshift({ label: 'Search', value: vendorName });
        return { filters };
    }

    return null;
};

const parseWorkItemIdFallback = (
    workItemId?: string,
    meta?: Record<string, unknown> | null,
): Record<string, unknown> | null => {
    const trimmed = String(workItemId || '').trim();
    if (!trimmed) return null;

    if (trimmed.startsWith('group:')) {
        const parts = trimmed.split(':');
        const direction = String(parts[1] || '').trim().toLowerCase();
        const normalizedDescription = parts.slice(2).join(':').trim();
        if (!normalizedDescription) return null;
        return {
            work_item_id: trimmed,
            item_type: 'alias_group',
            direction,
            normalized_description: normalizedDescription,
            vendor_name: String(meta?.vendor_name || '').trim(),
            transaction_ids: Array.isArray(meta?.transaction_ids) ? meta?.transaction_ids : [],
            document_ids: Array.isArray(meta?.document_ids) ? meta?.document_ids : [],
        };
    }

    if (trimmed.startsWith('txn:')) {
        return {
            work_item_id: trimmed,
            item_type: 'transaction_item',
            transaction_id: trimmed.slice(4),
            vendor_name: String(meta?.vendor_name || '').trim(),
            normalized_description: String(meta?.normalized_description || '').trim(),
            direction: String(meta?.direction || '').trim().toLowerCase(),
            transaction_ids: Array.isArray(meta?.transaction_ids) ? meta?.transaction_ids : [],
            document_ids: Array.isArray(meta?.document_ids) ? meta?.document_ids : [],
        };
    }

    return null;
};

const humanizeEvidenceKind = (kind?: string): string => {
    const normalized = String(kind || '').trim().toLowerCase();
    const labels: Record<string, string> = {
        amount_history: 'Previously verified bank transaction',
        transaction_history: 'Previously verified bank transaction',
        alias_history: 'Vendor alias history',
        qbo_transaction_history: 'QBO transaction history',
        qbo_gl_history: 'QBO general ledger history',
        bank_qbo_links: 'Linked bank and QBO records',
        bank_raw_context: 'Raw bank description',
        recurrence: 'Recurring pattern history',
        business_memory: 'Business memory',
    };
    return labels[normalized] || normalized.replace(/_/g, ' ') || 'Evidence';
};

const formatCurrency = (value: unknown): string => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '';
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const humanizeDiagnosisValue = (value?: string): string => {
    const normalized = String(value || '').trim();
    return normalized ? normalized.replace(/_/g, ' ') : '';
};

const formatEvidenceMetaValue = (key: string, value: unknown): string => {
    if (typeof value === 'number' && key.includes('amount')) return formatCurrency(value);
    if (Array.isArray(value)) return value.map((item) => String(item)).join(', ');
    return String(value);
};

const buildEvidenceDetailLines = (item: LedgerAgentCandidateEvidence): string[] => {
    const meta = item.meta && typeof item.meta === 'object'
        ? item.meta as Record<string, unknown>
        : {};
    const lines: string[] = [];
    const sourceType = String(meta.match_source_type || '').trim();
    const matchedLabel = String(meta.matched_vendor_name || meta.matched_normalized_description || '').trim();
    const matchedTxnId = String(meta.matched_transaction_id || '').trim();
    const matchedDate = String(meta.matched_date || '').trim();
    const matchedAmount = formatCurrency(meta.matched_amount_abs);
    const currentLabel = String(meta.current_vendor_name || meta.current_normalized_description || '').trim();
    const currentTxnId = String(meta.current_transaction_id || '').trim();
    const currentDate = String(meta.current_date || '').trim();
    const currentAmount = formatCurrency(meta.current_amount_abs);
    const distance = formatCurrency(meta.amount_distance_abs);

    if (item.kind === 'amount_history') {
        if (currentLabel || currentDate || currentAmount || currentTxnId) {
            lines.push(
                [
                    currentLabel ? `Current: ${currentLabel}` : 'Current transaction',
                    currentDate,
                    currentAmount,
                    currentTxnId ? `ID ${currentTxnId}` : '',
                ].filter(Boolean).join(' • ')
            );
        }
        if (matchedLabel || matchedDate || matchedAmount || matchedTxnId) {
            lines.push(
                [
                    `Matched against ${sourceType === 'verified_bank_transaction' ? 'verified bank transaction' : 'reference transaction'}`,
                    matchedLabel || 'Matched record',
                    matchedDate,
                    matchedAmount,
                    matchedTxnId ? `ID ${matchedTxnId}` : '',
                ].filter(Boolean).join(' • ')
            );
        }
        if (distance) {
            lines.push(`Amount difference: ${distance}`);
        }
        return lines;
    }

    Object.entries(meta)
        .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
        .slice(0, 4)
        .forEach(([key, value]) => {
            lines.push(`${key.replace(/_/g, ' ')}: ${formatEvidenceMetaValue(key, value)}`);
        });
    return lines;
};

const appendActivityStep = (
    steps: { type: string; message: string; status: 'active' | 'done' }[],
    event: { type: string; message: string },
) => {
    const completed = steps.map((step) => ({ ...step, status: 'done' as const }));
    const last = completed[completed.length - 1];
    if (last && last.type === event.type && last.message === event.message) {
        return completed;
    }
    return [
        ...completed,
        {
            type: event.type,
            message: event.message,
            status: 'active' as const,
        },
    ];
};

const finalizeActivitySteps = (
    steps: { type: string; message: string; status: 'active' | 'done' }[],
) => steps.map((step) => ({ ...step, status: 'done' as const }));

const buildAgentNarration = (job?: LedgerAgentJob | null) => {
    if (!job) return null;
    const events = job.progress_events || [];
    const latest = events[events.length - 1];
    const stats = (job.stats || {}) as Record<string, number>;
    const found = Number(stats.found_transactions || 0);
    const processed = Number(stats.processed || 0);
    const verified = Number(stats.verified || 0);
    const awaitingApproval = Number(stats.awaiting_approval || 0);
    const needsReview = Number(stats.needs_review || 0);
    const tools = Array.isArray(latest?.meta?.tools) ? latest.meta?.tools as string[] : [];
    const workItemLabel = typeof latest?.meta?.work_item_id === 'string' ? latest.meta.work_item_id : '';

    if (!latest) {
        return {
            title: 'Agent Update',
            body: 'I am getting ready to review your ledger data.',
        };
    }

    switch (latest.stage) {
        case 'starting':
            return {
                title: 'Agent Update',
                body: 'I am checking your ledger data and preparing the needs review queue.',
            };
        case 'planning':
            return {
                title: 'Agent Update',
                body: 'I loaded the ledger rules, memory, and tool catalog so I can investigate these items safely.',
            };
        case 'scanning':
            return {
                title: 'Agent Update',
                body: found > 0
                    ? `I found ${found} needs review transactions. I am sorting out the repeat patterns first so I can handle them more efficiently.`
                    : 'I am scanning for needs review transactions now.',
            };
        case 'grouping':
            return {
                title: 'Agent Update',
                body: `I grouped the current backlog into work items so I can resolve repeated vendors together and keep generic descriptors separate.`,
            };
        case 'planning_tools':
            return {
                title: 'Agent Update',
                body: 'I am choosing the best evidence path for this item before I decide whether it is safe to apply a change.',
            };
        case 'processing':
            return {
                title: 'Agent Update',
                body: workItemLabel
                    ? `I am investigating ${workItemLabel}. I am comparing historical matches before I decide whether it is safe to apply a change.`
                    : 'I am investigating the next work item now.',
            };
        case 'solving':
            return {
                title: 'Agent Update',
                body: `I have processed ${processed} items so far. Verified ${verified}, moved ${awaitingApproval} to awaiting approval, and left ${needsReview} in needs review where the evidence is still weak.`,
            };
        case 'awaiting_user':
            return {
                title: 'Agent Update',
                body: 'I found a plausible resolution, but the evidence is not clean enough for me to apply it on my own. I need your confirmation on this one.',
            };
        case 'awaiting_continue':
            return {
                title: 'Agent Update',
                body: `I got through ${processed} items in this run and saved the progress. If you want, I can continue from the remaining queue.`,
            };
        case 'failed':
            return {
                title: 'Agent Update',
                body: typeof latest.message === 'string' && latest.message
                    ? `I hit a processing error on this run. ${latest.message}`
                    : 'I hit a processing error on this run, but I kept the progress up to that point.',
            };
        default:
            return {
                title: 'Agent Update',
                body: latest.message || 'I am working through the current ledger items.',
            };
    }
};

const isQueryJob = (job?: LedgerAgentJob | null) => {
    const spec = (job?.spec || {}) as Record<string, unknown>;
    return spec.job_mode === 'bulk_operation' && spec.operation_action === 'query';
};

const shouldAutoShowAgentDetails = ({
    message,
    job,
    checkpoint,
}: {
    message: DisplayMessage;
    job?: LedgerAgentJob | null;
    checkpoint?: LedgerAgentCheckpoint | null;
}) => {
    if (message.meta?.general_chat || message.meta?.source === 'general') return false;
    if (checkpoint) return true;
    if (!job) return false;
    if (job.status === 'running' || job.status === 'queued' || job.status === 'failed') return true;
    if (isQueryJob(job)) return false;
    return Boolean(job.progress_events?.length);
};

const hasAgentDetails = ({
    message,
    job,
    checkpoint,
}: {
    message: DisplayMessage;
    job?: LedgerAgentJob | null;
    checkpoint?: LedgerAgentCheckpoint | null;
}) => {
    if (message.meta?.general_chat || message.meta?.source === 'general') return false;

    const hasSummary = Boolean(String(job?.result?.summary || '').trim());
    const hasStats = Object.keys((job?.stats || {}) as Record<string, unknown>).length > 0;
    const hasEvents = (job?.progress_events || []).length > 0;

    return Boolean(checkpoint || buildAgentNarration(job) || hasSummary || hasStats || hasEvents);
};

const MessageBubble: React.FC<{
    message: DisplayMessage;
    onSuggestionClick?: (question: string) => void;
    onActionClick?: (actionType: string, payload?: Record<string, unknown>) => void;
    onOpenTransaction?: (documentId: string) => void;
    suppressBody?: boolean;
}> = ({ message, onSuggestionClick, onActionClick, onOpenTransaction, suppressBody = false }) => {
    const [showActivity, setShowActivity] = useState(false);
    const [relatedTransactionsPage, setRelatedTransactionsPage] = useState(0);
    const activityWasActiveRef = useRef(false);
    const activity = message.activity || [];
    const inlineActions = ((message.meta?.actions as LedgerAgentAction[]) || []);
    const businessMemory = message.meta?.business_memory as { vendor_name?: string; preferred_mapping?: { fiscal_bucket?: string; account_name?: string } } | undefined;
    const resultSet = message.meta?.result_set as { summary_preview?: ResultPreviewRow[]; filters?: ResultFilterChip[] } | undefined;
    const previewRows = (resultSet?.summary_preview || []) as ResultPreviewRow[];
    const messageDocumentIds = ((message.meta?.document_ids as string[]) || []).filter(Boolean);
    const messageTransactionIds = ((message.meta?.transaction_ids as string[]) || []).filter(Boolean);
    const relatedTransactionIds = messageDocumentIds.length > 0 ? messageDocumentIds : messageTransactionIds;
    const relatedTransactionsPerPage = 4;
    const relatedTransactionsPageCount = Math.max(1, Math.ceil(relatedTransactionIds.length / relatedTransactionsPerPage));
    const visibleRelatedTransactionIds = relatedTransactionIds.slice(
        relatedTransactionsPage * relatedTransactionsPerPage,
        (relatedTransactionsPage + 1) * relatedTransactionsPerPage,
    );
    const hasResultTransactions = previewRows.length > 0 || messageDocumentIds.length > 0 || messageTransactionIds.length > 0;
    const visibleInlineActions = inlineActions.filter((action) => {
        if (action.action_type === 'show_alternates') return false;
        if (action.action_type === 'confirm_bulk_operation' || action.action_type === 'skip_item') return false;
        if (isCandidateActionType(action.action_type)) return false;
        if (action.action_type === 'continue_guided_remediation' || action.action_type === 'stop_guided_remediation') return false;
        if (!action.action_type.startsWith('apply_last_result_')) return true;
        return hasResultTransactions;
    });

    useEffect(() => {
        setRelatedTransactionsPage(0);
    }, [messageDocumentIds.join(','), messageTransactionIds.join(',')]);

    useEffect(() => {
        const hasActive = activity.some((item) => item.status === 'active');
        if (hasActive) {
            activityWasActiveRef.current = true;
            setShowActivity(true);
            return;
        }
        if (activity.length > 0 && activityWasActiveRef.current) {
            setShowActivity(false);
            activityWasActiveRef.current = false;
            return;
        }
        if (activity.length === 0) {
            activityWasActiveRef.current = false;
        }
    }, [activity]);

    if (message.role === 'human') {
        return (
            <div className="flex w-full justify-end">
                <div className="p-4 w-fit bg-gray-100 rounded-lg text-gray-900">
                    {message.content}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-3 text-black">
            {activity.length > 0 && (
                <div className="rounded-full border border-gray-200 bg-gray-50 px-3 py-2">
                    <button
                        type="button"
                        onClick={() => setShowActivity((prev) => !prev)}
                        className="flex w-full items-center justify-between gap-3 text-left"
                    >
                        <div className="min-w-0 flex items-center gap-2">
                            {activity[activity.length - 1]?.status === 'active' ? (
                                <LoaderCircle className="h-3.5 w-3.5 animate-spin text-gray-500" />
                            ) : (
                                <CheckCircle2 className="h-3.5 w-3.5 text-gray-500" />
                            )}
                            <div className="truncate text-[12px] text-gray-600">
                                {activity[activity.length - 1]?.message}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-500">
                            <span>{activity.length} step{activity.length === 1 ? '' : 's'}</span>
                            {showActivity ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </div>
                    </button>
                </div>
            )}

            {!suppressBody && <ReactMarkdown>{message.content}</ReactMarkdown>}

            {showActivity && activity.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                    <div className="space-y-2">
                        {activity.map((step, index) => {
                            const isLast = index === activity.length - 1;
                            return (
                                <div key={`${step.type}-${step.message}-${index}`} className="flex gap-3">
                                    <div className="flex flex-col items-center">
                                        <div className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white">
                                            {step.status === 'active' ? (
                                                <LoaderCircle className="h-3.5 w-3.5 animate-spin text-gray-500" />
                                            ) : (
                                                <CheckCircle2 className="h-3.5 w-3.5 text-gray-500" />
                                            )}
                                        </div>
                                        {!isLast && <div className="mt-1 h-4 w-px bg-gray-200" />}
                                    </div>
                                    <div className="pt-0.5">
                                        <div className="text-[13px] text-gray-700">{step.message}</div>
                                        <div className="mt-0.5 text-[11px] text-gray-400">
                                            {step.status === 'active' ? 'In progress' : 'Completed'}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {!suppressBody && !resultSet && relatedTransactionIds.length > 0 && onOpenTransaction && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-gray-500">
                            Related Transactions
                        </div>
                        {relatedTransactionIds.length > relatedTransactionsPerPage && (
                            <div className="text-[11px] text-gray-500">
                                {relatedTransactionsPage * relatedTransactionsPerPage + 1}
                                -
                                {Math.min((relatedTransactionsPage + 1) * relatedTransactionsPerPage, relatedTransactionIds.length)}
                                {' '}of {relatedTransactionIds.length}
                            </div>
                        )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {visibleRelatedTransactionIds.map((transactionId) => (
                            <button
                                key={transactionId}
                                type="button"
                                onClick={() => onOpenTransaction(transactionId)}
                                className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-700 hover:bg-gray-100"
                            >
                                View transaction
                            </button>
                        ))}
                    </div>
                    {relatedTransactionIds.length > relatedTransactionsPerPage && (
                        <div className="mt-3 flex items-center justify-between gap-3">
                            <div className="text-[11px] text-gray-500">
                                Showing {visibleRelatedTransactionIds.length} transaction{visibleRelatedTransactionIds.length === 1 ? '' : 's'} on this page.
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setRelatedTransactionsPage((prev) => Math.max(0, prev - 1))}
                                    disabled={relatedTransactionsPage === 0}
                                    className="inline-flex items-center gap-1 px-1 py-1 text-[11px] text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:text-gray-300"
                                >
                                    <ChevronLeft className="h-3.5 w-3.5" />
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRelatedTransactionsPage((prev) => Math.min(relatedTransactionsPageCount - 1, prev + 1))}
                                    disabled={relatedTransactionsPage >= relatedTransactionsPageCount - 1}
                                    className="inline-flex items-center gap-1 px-1 py-1 text-[11px] text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:text-gray-300"
                                >
                                    Next
                                    <ChevronRight className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {!suppressBody && businessMemory?.preferred_mapping && (
                <div className="space-y-2">
                    {businessMemory?.preferred_mapping && (
                        <div className="text-xs text-gray-500">
                            Prior business memory: {businessMemory.vendor_name || 'vendor'}{businessMemory.preferred_mapping.fiscal_bucket ? ` -> ${businessMemory.preferred_mapping.fiscal_bucket}` : ''}{businessMemory.preferred_mapping.account_name ? ` / ${businessMemory.preferred_mapping.account_name}` : ''}
                        </div>
                    )}
                </div>
            )}

            {!suppressBody && message.suggested_questions && message.suggested_questions.length > 0 && (
                <div className="flex flex-col gap-2 mt-4 pt-2">
                    <div className="flex flex-col items-start gap-3 w-full">
                        {message.suggested_questions.map((question, idx) => (
                            <button
                                key={`${question}-${idx}`}
                                onClick={() => onSuggestionClick?.(question)}
                                className="min-h-[45px] px-4 py-2 border border-gray-300 rounded-lg text-sm text-left w-auto max-w-full transition-all bg-white text-gray-700 hover:bg-gray-100 hover:border-gray-300"
                            >
                                {question}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {!suppressBody && visibleInlineActions.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4 pt-2">
                    {visibleInlineActions.map((action, idx) => (
                        <button
                            key={`${action.action_type}-${idx}`}
                            type="button"
                            onClick={() => onActionClick?.(action.action_type, action.payload)}
                            className={action.variant === 'primary'
                                ? 'rounded-md bg-gray-900 px-3 py-2 text-sm text-white hover:bg-gray-800'
                                : 'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-100'}
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const buildFocusContextLabel = (session?: LedgerAgentSession | null) => {
    const focus = session?.working_memory?.last_focus;
    if (!focus || typeof focus !== 'object') return null;
    const selectedTransaction = focus.selected_transaction && typeof focus.selected_transaction === 'object'
        ? focus.selected_transaction as Record<string, unknown>
        : null;
    if (selectedTransaction) {
        const date = String(selectedTransaction.date || '').trim();
        const originalDescription = String(selectedTransaction.original_description || '').trim();
        const normalizedDescription = String(selectedTransaction.normalized_description || '').trim();
        const vendorName = String(selectedTransaction.vendor_name || '').trim();
        const primaryLabel = originalDescription || normalizedDescription || vendorName;
        if (date && primaryLabel) {
            return `Focused on ${date} • ${primaryLabel}`;
        }
        if (primaryLabel) {
            return `Focused on ${primaryLabel}`;
        }
    }
    const filters = Array.isArray(focus.filters) ? focus.filters.filter((item) => item?.label && item?.value) : [];
    const transactionCount = Array.isArray(focus.document_ids) && focus.document_ids.length > 0
        ? focus.document_ids.length
        : Array.isArray(focus.transaction_ids)
            ? focus.transaction_ids.length
            : 0;

    if (filters.length === 0 && transactionCount === 0) return null;

    const filterSummary = filters.slice(0, 3).map((item) => `${item.label}: ${item.value}`).join(' • ');

    if (transactionCount > 0 && filterSummary) {
        return `Working on ${transactionCount} transactions • ${filterSummary}`;
    }
    if (transactionCount > 0) {
        return `Working on ${transactionCount} transactions`;
    }
    return filterSummary || null;
};

const buildCurrentWorkItemLabel = (workItem?: Record<string, unknown> | null) => {
    if (!workItem) return null;
    const transactionCount = Array.isArray(workItem.document_ids) && workItem.document_ids.length > 0
        ? workItem.document_ids.length
        : Array.isArray(workItem.transaction_ids)
            ? workItem.transaction_ids.length
            : Number(workItem.group_size || 0);
    const vendorName = String(workItem.vendor_name || '').trim();
    const normalizedDescription = String(workItem.normalized_description || '').trim();
    const direction = String(workItem.direction || '').trim().toLowerCase();
    const label = !isGenericWorkItemLabel(vendorName) && vendorName
        ? vendorName
        : normalizedDescription || vendorName;
    const parts = [
        transactionCount > 0 ? `Working on ${transactionCount} transaction${transactionCount === 1 ? '' : 's'}` : '',
        label,
        direction === 'inflow' || direction === 'outflow' ? direction : '',
    ].filter(Boolean);
    return parts.join(' • ') || null;
};

const buildJobStatusLabel = (job?: LedgerAgentJob | null) => {
    if (!job) return null;
    if (isTransientAgentJob(job)) return 'Continuing';
    const latest = (job.progress_events || [])[job.progress_events!.length - 1];
    const stage = String(latest?.stage || job.status || '').trim();
    if (!stage) return null;
    const labels: Record<string, string> = {
        queued: 'Queued',
        starting: 'Starting',
        planning: 'Planning',
        querying: 'Checking data',
        scanning: 'Scanning',
        grouping: 'Grouping',
        planning_tools: 'Planning tools',
        processing: 'Reviewing',
        solving: 'Applying',
        awaiting_user: 'Waiting for confirmation',
        awaiting_continue: 'Waiting to continue',
        completed: 'Completed',
        failed: 'Failed',
        running: 'Running',
    };
    return labels[stage] || stage.replace(/_/g, ' ');
};

const getGuidedRemediationProgress = (job?: LedgerAgentJob | null) => {
    if (!job) return null;
    const result = (job.result || {}) as Record<string, unknown>;
    if (result.mode !== 'guided_remediation') return null;
    const queueState = (result.queue_state && typeof result.queue_state === 'object')
        ? result.queue_state as Record<string, unknown>
        : null;
    if (!queueState) return null;
    const workItems = Array.isArray(queueState.work_items) ? queueState.work_items : [];
    const currentIndex = Number(queueState.current_index || 0);
    const currentWorkItem = (result.current_work_item && typeof result.current_work_item === 'object')
        ? result.current_work_item as Record<string, unknown>
        : null;
    const currentLabel = String(
        currentWorkItem?.vendor_name
        || currentWorkItem?.normalized_description
        || currentWorkItem?.work_item_id
        || ''
    ).trim();
    return {
        currentIndex,
        total: workItems.length,
        currentLabel,
    };
};

const AISparkle = ({ size = 17 }: { size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="shrink-0"
    >
        <path
            d="M16.2 8.00833L13.7 6.75833L11.4667 5.64167L11.4417 5.61667L10.325 3.38333L9.075 0.883333C8.79167 0.316667 7.86667 0.316667 7.58333 0.883333L6.33333 3.38333L5.21667 5.61667L5.19167 5.64167L2.95833 6.75833L0.458333 8.00833C0.175 8.15 0 8.44167 0 8.75C0 9.05833 0.175 9.35 0.458333 9.49167L2.95833 10.7417L5.19167 11.8583L5.21667 11.8833L6.33333 14.1167L7.58333 16.6167C7.725 16.9 8.01667 17.075 8.325 17.075C8.63333 17.075 8.925 16.9 9.06667 16.6167L10.3167 14.1167L11.4333 11.8833L11.4583 11.8583L13.6917 10.7417L16.1917 9.49167C16.475 9.35 16.65 9.05833 16.65 8.75C16.65 8.44167 16.475 8.15 16.1917 8.00833H16.2ZM14.575 0L13.7917 1.71667L12.075 2.5L13.7917 3.28333L14.575 5L15.3583 3.28333L17.075 2.5L15.3583 1.71667L14.575 0Z"
            fill="url(#ledger_ai_sparkle_gradient)"
        />
        <defs>
            <linearGradient
                id="ledger_ai_sparkle_gradient"
                x1="0.332256"
                y1="0"
                x2="17.3938"
                y2="16.3976"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor="#4143FF" />
                <stop offset="1" stopColor="#9798FC" />
            </linearGradient>
        </defs>
    </svg>
);

const ActivityCard: React.FC<{ job?: LedgerAgentJob | null; diagnosis?: LedgerDiagnosisSummary | null }> = ({ job, diagnosis }) => {
    const [expanded, setExpanded] = useState(false);
    const events = job?.progress_events || [];
    const normalizedStatus = String(job?.status || '').trim().toLowerCase();

    useEffect(() => {
        if (['queued', 'running'].includes(normalizedStatus)) {
            setExpanded(true);
            return;
        }
        setExpanded(false);
    }, [normalizedStatus, job?._id]);

    if (!job || events.length === 0) return null;

    const renderEventMeta = (event: LedgerAgentJobEvent) => {
        const meta = event.meta || {};
        const details: string[] = [];
        const tools = Array.isArray(meta.tools) ? meta.tools.filter(Boolean).map(String) : [];
        const interpretation = meta.interpretation && typeof meta.interpretation === 'object'
            ? meta.interpretation as Record<string, unknown>
            : null;
        const observations = meta.observations && typeof meta.observations === 'object'
            ? meta.observations as Record<string, unknown>
            : null;
        const filters = Array.isArray(meta.filters)
            ? meta.filters
                .map((item) => {
                    if (!item || typeof item !== 'object') return '';
                    const record = item as Record<string, unknown>;
                    const label = String(record.label || '').trim();
                    const value = String(record.value || '').trim();
                    return label && value ? `${label}: ${value}` : '';
                })
                .filter(Boolean)
            : [];
        const updates = meta.updates && typeof meta.updates === 'object'
            ? Object.entries(meta.updates as Record<string, unknown>)
                .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
                .map(([key, value]) => `${key}: ${String(value)}`)
            : [];
        const selectedTools = Array.isArray(meta.selected_tools) ? meta.selected_tools.filter(Boolean).map(String) : [];
        const operationDecision = meta.operation_decision && typeof meta.operation_decision === 'object'
            ? meta.operation_decision as Record<string, unknown>
            : null;
        const evidenceContext = meta.evidence_context && typeof meta.evidence_context === 'object'
            ? meta.evidence_context as Record<string, unknown>
            : null;

        if (typeof meta.action === 'string' && meta.action) details.push(`Action: ${meta.action}`);
        if (typeof meta.planner_action === 'string' && meta.planner_action) details.push(`Planner action: ${meta.planner_action}`);
        if (interpretation && typeof interpretation.route === 'string' && interpretation.route) {
            details.push(`Interpreted route: ${interpretation.route}`);
        }
        if (observations) {
            const observationParts = [
                typeof observations.has_result_set === 'boolean' ? `has_result_set=${String(observations.has_result_set)}` : '',
                typeof observations.has_active_job === 'boolean' ? `has_active_job=${String(observations.has_active_job)}` : '',
                typeof observations.has_checkpoint === 'boolean' ? `has_checkpoint=${String(observations.has_checkpoint)}` : '',
            ].filter(Boolean);
            if (observationParts.length > 0) details.push(`Observations: ${observationParts.join(' • ')}`);
        }
        if (typeof meta.source === 'string' && meta.source) details.push(`Plan source: ${meta.source}`);
        if (typeof meta.reasoning === 'string' && meta.reasoning) details.push(`Reasoning: ${meta.reasoning}`);
        if (tools.length > 0) details.push(`Tools: ${tools.join(', ')}`);
        if (selectedTools.length > 0) details.push(`Selected tools: ${selectedTools.join(', ')}`);
        if (operationDecision) {
            const decisionParts = [
                typeof operationDecision.mode === 'string' ? `mode=${operationDecision.mode}` : '',
                typeof operationDecision.requires_confirmation === 'boolean' ? `requires_confirmation=${String(operationDecision.requires_confirmation)}` : '',
            ].filter(Boolean);
            if (decisionParts.length > 0) details.push(`Operation decision: ${decisionParts.join(' • ')}`);
        }
        if (filters.length > 0) details.push(`Filters: ${filters.join(' • ')}`);
        if (updates.length > 0) details.push(`Updates: ${updates.join(' • ')}`);
        if (evidenceContext) {
            const evidenceParts = [
                typeof evidenceContext.has_result_set === 'boolean' ? `has_result_set=${String(evidenceContext.has_result_set)}` : '',
                typeof evidenceContext.vendor_name === 'string' && evidenceContext.vendor_name ? `vendor=${evidenceContext.vendor_name}` : '',
                evidenceContext.has_business_memory ? 'business_memory=true' : '',
                evidenceContext.resolved_account && typeof evidenceContext.resolved_account === 'object'
                    ? `resolved_account=${String((evidenceContext.resolved_account as Record<string, unknown>).account_name || (evidenceContext.resolved_account as Record<string, unknown>).fiscal_bucket || '')}`
                    : '',
            ].filter(Boolean);
            if (evidenceParts.length > 0) details.push(`Evidence: ${evidenceParts.join(' • ')}`);
        }
        if (typeof meta.row_count === 'number') details.push(`Matches: ${meta.row_count}`);

        if (diagnosis) {
            if (diagnosis.issue_type) details.push(`Diagnosis: ${humanizeDiagnosisValue(diagnosis.issue_type)}`);
            if (diagnosis.observed_problem) details.push(`Observed problem: ${diagnosis.observed_problem}`);
            if (diagnosis.recommended_vendor_name) details.push(`Suggested vendor: ${diagnosis.recommended_vendor_name}`);
            if (diagnosis.recommended_action) details.push(`Next step: ${humanizeDiagnosisValue(diagnosis.recommended_action)}`);
            if (typeof diagnosis.confidence === 'number' && Number.isFinite(diagnosis.confidence)) {
                details.push(`Confidence: ${Math.round(diagnosis.confidence * 100)}%`);
            }
        }

        if (details.length === 0) return null;

        return (
            <div className="mt-1.5 space-y-1">
                {details.map((detail, index) => (
                    <div key={`${event.id}-detail-${index}`} className="text-[11px] leading-4 text-gray-500">
                        {detail}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50">
            <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="flex w-full items-center justify-between px-3 py-2 text-left"
            >
                <div className="min-w-0">
                    <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-gray-500">
                        Ledger Agent Activity
                    </div>
                    <div className="mt-0.5 truncate text-[13px] text-gray-600">
                        {events[events.length - 1]?.message || 'Working'}
                    </div>
                </div>
                <div className="ml-3 flex items-center gap-2 text-[11px] text-gray-500">
                    <span>{job.status}</span>
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </div>
            </button>

            {expanded && (
                <div className="border-t border-gray-200 px-3 py-2">
                    <div className="space-y-2">
                        {events.slice(-8).map((event, index) => {
                            const isLast = index === events.slice(-8).length - 1;
                            const isRunning = job.status === 'running' && isLast;
                            return (
                                <div key={event.id} className="flex gap-3">
                                    <div className="flex flex-col items-center">
                                        <div className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white">
                                            {isRunning ? (
                                                <LoaderCircle className="h-3.5 w-3.5 animate-spin text-gray-500" />
                                            ) : (
                                                <CheckCircle2 className="h-3.5 w-3.5 text-gray-500" />
                                            )}
                                        </div>
                                        {!isLast && <div className="mt-1 h-4 w-px bg-gray-200" />}
                                    </div>
                                    <div className="pt-0.5">
                                        <div className="text-[13px] text-gray-700">{event.message}</div>
                                        {renderEventMeta(event)}
                                        <div className="mt-0.5 text-[11px] text-gray-400">
                                            {isRunning ? 'In progress' : 'Completed'}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const TurnTraceCard: React.FC<{ session?: LedgerAgentSession | null }> = ({ session }) => {
    const trace = session?.working_memory?.last_turn_trace as Record<string, unknown> | null | undefined;
    if (!trace || typeof trace !== 'object') return null;

    const interpretation = trace.interpretation && typeof trace.interpretation === 'object'
        ? trace.interpretation as Record<string, unknown>
        : null;
    const planner = trace.planner && typeof trace.planner === 'object'
        ? trace.planner as Record<string, unknown>
        : null;
    const observations = trace.observations && typeof trace.observations === 'object'
        ? trace.observations as Record<string, unknown>
        : null;
    const normalizedIntent = trace.normalized_intent && typeof trace.normalized_intent === 'object'
        ? trace.normalized_intent as Record<string, unknown>
        : null;
    const toolObservations = trace.tool_observations && typeof trace.tool_observations === 'object'
        ? trace.tool_observations as Record<string, unknown>
        : null;
    const clarification = trace.clarification && typeof trace.clarification === 'object'
        ? trace.clarification as Record<string, unknown>
        : null;
    const toolCalls = Array.isArray(trace.tool_calls) ? trace.tool_calls.filter(Boolean).map(String) : [];

    const lines: string[] = [];
    if (typeof trace.raw_instruction === 'string' && trace.raw_instruction) lines.push(`Instruction: ${trace.raw_instruction}`);
    if (typeof trace.route === 'string' && trace.route) lines.push(`Route: ${trace.route}`);
    if (interpretation && typeof interpretation.reasoning === 'string' && interpretation.reasoning) {
        lines.push(`Interpretation reasoning: ${interpretation.reasoning}`);
    }
    if (normalizedIntent) {
        const intentParts = [
            typeof normalizedIntent.action === 'string' ? `action=${normalizedIntent.action}` : '',
            typeof normalizedIntent.vendor_name === 'string' && normalizedIntent.vendor_name ? `vendor=${normalizedIntent.vendor_name}` : '',
            typeof normalizedIntent.exclude_fiscal_bucket === 'string' && normalizedIntent.exclude_fiscal_bucket ? `exclude_bucket=${normalizedIntent.exclude_fiscal_bucket}` : '',
            typeof normalizedIntent.target_status === 'string' && normalizedIntent.target_status ? `target_status=${normalizedIntent.target_status}` : '',
            typeof normalizedIntent.target_fiscal_bucket === 'string' && normalizedIntent.target_fiscal_bucket ? `target_bucket=${normalizedIntent.target_fiscal_bucket}` : '',
        ].filter(Boolean);
        if (intentParts.length > 0) lines.push(`Normalized intent: ${intentParts.join(' • ')}`);
    }
    if (planner) {
        const plannerParts = [
            typeof planner.action_type === 'string' ? `action=${planner.action_type}` : '',
            typeof planner.confidence === 'number' ? `confidence=${planner.confidence}` : '',
            typeof planner.reasoning === 'string' && planner.reasoning ? `reasoning=${planner.reasoning}` : '',
        ].filter(Boolean);
        if (plannerParts.length > 0) lines.push(`Planner: ${plannerParts.join(' • ')}`);
    }
    if (observations) {
        const observationParts = [
            typeof observations.has_result_set === 'boolean' ? `has_result_set=${String(observations.has_result_set)}` : '',
            typeof observations.has_active_job === 'boolean' ? `has_active_job=${String(observations.has_active_job)}` : '',
            typeof observations.has_checkpoint === 'boolean' ? `has_checkpoint=${String(observations.has_checkpoint)}` : '',
        ].filter(Boolean);
        if (observationParts.length > 0) lines.push(`Observations: ${observationParts.join(' • ')}`);
    }
    if (toolCalls.length > 0) lines.push(`Tool calls: ${toolCalls.join(', ')}`);
    if (toolObservations) {
        const toolObservationParts = [
            typeof toolObservations.has_result_set === 'boolean' ? `has_result_set=${String(toolObservations.has_result_set)}` : '',
            typeof toolObservations.vendor_name === 'string' && toolObservations.vendor_name ? `vendor=${toolObservations.vendor_name}` : '',
            toolObservations.resolved_account && typeof toolObservations.resolved_account === 'object'
                ? `resolved_account=${String((toolObservations.resolved_account as Record<string, unknown>).account_name || (toolObservations.resolved_account as Record<string, unknown>).fiscal_bucket || '')}`
                : '',
        ].filter(Boolean);
        if (toolObservationParts.length > 0) lines.push(`Tool observations: ${toolObservationParts.join(' • ')}`);
    }
    if (typeof trace.final_action === 'string' && trace.final_action) lines.push(`Final action: ${trace.final_action}`);
    if (clarification && clarification.needed) {
        lines.push(`Clarification: ${String(clarification.prompt || 'needed')}`);
    }
    if (typeof trace.result_summary === 'string' && trace.result_summary) {
        lines.push(`Result summary: ${trace.result_summary}`);
    }

    if (lines.length === 0) return null;

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2 shadow-sm">
            <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-gray-500">
                Turn Trace
            </div>
            {lines.map((line, index) => (
                <div key={`trace-line-${index}`} className="text-[12px] leading-5 text-gray-700">
                    {line}
                </div>
            ))}
        </div>
    );
};

const AgentStatusBar: React.FC<{
    job?: LedgerAgentJob | null;
    resumingCheckpoint?: boolean;
    onStop?: () => void;
}> = ({ job, resumingCheckpoint = false, onStop }) => {
    const events = job?.progress_events || [];
    const latestEvent = events.length > 0 ? events[events.length - 1] : undefined;
    const guidedProgress = getGuidedRemediationProgress(job);
    const normalizedJobStatus = String(job?.status || '').toLowerCase();
    const statusLabel = resumingCheckpoint
        ? 'Continuing to the next item'
        : guidedProgress && latestEvent?.stage === 'awaiting_user'
            ? 'Waiting on this group before continuing'
            : buildJobStatusLabel(job);
    const stats = (job?.stats || {}) as Record<string, number>;
    const summaryBits = [
        typeof stats.found_transactions === 'number' && stats.found_transactions > 0 ? `${stats.found_transactions} checked` : '',
        typeof stats.verified === 'number' && stats.verified > 0 ? `${stats.verified} verified` : '',
        typeof stats.awaiting_approval === 'number' && stats.awaiting_approval > 0 ? `${stats.awaiting_approval} awaiting approval` : '',
        typeof stats.needs_review === 'number' && stats.needs_review > 0 ? `${stats.needs_review} still needs review` : '',
    ].filter(Boolean);
    if (!job) return null;

    return (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-gray-500">
                        Agent Status
                    </div>
                    <div className="mt-1 text-[13px] font-medium text-gray-900">
                        {statusLabel || 'Working'}
                    </div>
                    <div className="mt-1 text-[12px] text-gray-600">
                        {resumingCheckpoint
                            ? 'I saved your response and the worker is continuing through the remaining items.'
                            : guidedProgress && normalizedJobStatus === 'awaiting_user'
                                ? 'Review this group and choose what to do before the agent moves to the next one.'
                                : guidedProgress && normalizedJobStatus === 'awaiting_dispatch'
                                    ? 'This group is done. The next one will start automatically unless you stop the run.'
                                    : (latestEvent?.message || 'The agent is working through your request.')}
                    </div>
                    {summaryBits.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {summaryBits.map((bit) => (
                                <span key={bit} className="rounded-full border border-white/70 bg-white px-2 py-1 text-[11px] text-gray-600">
                                    {bit}
                                </span>
                            ))}
                        </div>
                    )}
                    {guidedProgress && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            <span className="rounded-full border border-white/70 bg-white px-2 py-1 text-[11px] text-gray-600">
                                Group {Math.min(guidedProgress.currentIndex + 1, guidedProgress.total || 1)} of {guidedProgress.total || 1}
                            </span>
                            {guidedProgress.currentLabel && (
                                <span className="rounded-full border border-white/70 bg-white px-2 py-1 text-[11px] text-gray-600">
                                    {guidedProgress.currentLabel}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                    {guidedProgress && job && ['queued', 'running', 'awaiting_dispatch', 'awaiting_user'].includes(String(job.status || '').toLowerCase()) && (
                        <button
                            type="button"
                            onClick={onStop}
                            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-100"
                        >
                            {normalizedJobStatus === 'awaiting_user' ? 'Stop after this group' : 'Stop'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const AgentNarrationCard: React.FC<{ job?: LedgerAgentJob | null }> = ({ job }) => {
    const narration = buildAgentNarration(job);

    if (!narration) return null;

    return (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/80 p-4">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-white p-1.5 shadow-sm">
                    <AISparkle size={14} />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-indigo-500">
                        {narration.title}
                    </div>
                    <div className="mt-1 text-[13px] leading-5 text-slate-700">
                        {narration.body}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ResultSummaryCard: React.FC<{
    job?: LedgerAgentJob | null;
    onContinue?: (instruction?: string) => void;
}> = ({ job, onContinue }) => {
    const summary = String(job?.result?.summary || '').trim();
    const stats = (job?.stats || {}) as Record<string, number>;
    const isGuidedRemediation = (job?.result as Record<string, unknown> | undefined)?.mode === 'guided_remediation';
    const normalizedStatus = String(job?.status || '').toLowerCase();
    const canContinue = Boolean(job?.result?.can_continue) && !String(job?.result?.continuation_instruction || job?.spec?.instruction || '').toLowerCase().includes('fix needs review');
    const continuationInstruction = typeof job?.result?.continuation_instruction === 'string'
        ? job.result.continuation_instruction
        : typeof job?.spec?.instruction === 'string'
            ? job.spec.instruction
            : '';

    if (!job || job.status === 'running' || job.status === 'queued' || (!summary && !stats)) {
        return null;
    }
    if (isGuidedRemediation && !['completed', 'stopped', 'failed', 'interrupted'].includes(normalizedStatus)) {
        return null;
    }

    const statItems = [
        { label: 'Checked', value: Number(stats.found_transactions || 0) },
        { label: 'Verified', value: Number(stats.verified || 0) },
        { label: 'Awaiting Approval', value: Number(stats.awaiting_approval || 0) },
        { label: 'Still Needs Review', value: Number(stats.needs_review || 0) },
    ];

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3 shadow-sm">
            <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-gray-500">
                Run Summary
            </div>

            <div className="grid grid-cols-2 gap-2">
                {statItems.map((item) => (
                    <div key={item.label} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                        <div className="text-[10px] text-gray-500">{item.label}</div>
                        <div className="mt-1 text-base font-semibold text-gray-900">{item.value}</div>
                    </div>
                ))}
            </div>

            {summary && (
                <div className="text-[13px] leading-5 text-gray-700">
                    {summary}
                </div>
            )}

            {canContinue && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2">
                    <div className="text-[13px] text-blue-900">
                        This run hit its current processing limit. Continue to keep working through the remaining items.
                    </div>
                    <button
                        type="button"
                        onClick={() => onContinue?.(continuationInstruction)}
                        className="shrink-0 rounded-md bg-gray-900 px-3 py-2 text-[13px] text-white hover:bg-gray-800"
                    >
                        Continue
                    </button>
                </div>
            )}
        </div>
    );
};

const CheckpointCard: React.FC<{
    checkpoint?: LedgerAgentCheckpoint | null;
    onAction: (actionType: string, payload?: Record<string, unknown>) => void;
}> = ({ checkpoint, onAction }) => {
    const [showAlternates, setShowAlternates] = useState(false);

    if (!checkpoint) return null;

    const payload = checkpoint.payload || {};
    const candidates = payload.candidates || [];
    const actions = payload.actions || [];
    const conflicts = payload.conflicts || [];
    const recommendedQuestion = payload.recommended_question;
    const normalizedPrompt = String(checkpoint.prompt || '').trim();
    const normalizedRecommendedQuestion = String(recommendedQuestion || '').trim();
    const showRecommendedQuestion = Boolean(
        normalizedRecommendedQuestion
        && normalizedRecommendedQuestion !== normalizedPrompt,
    );
    const candidateBundle = payload.candidate_bundle as { selected_tools?: string[]; tool_plan?: { source?: string; reasoning?: string } } | undefined;
    const businessMemory = payload.business_memory as { vendor_name?: string; preferred_mapping?: { fiscal_bucket?: string; account_name?: string } } | undefined;
    const previewRows = (payload.preview_rows || []) as ResultPreviewRow[];
    const topCandidate = candidates[0];
    const hasAlternates = candidates.length > 1;
    const visibleCandidates = showAlternates ? candidates : candidates.slice(0, 1);
    const visibleActions: LedgerAgentAction[] = [];

    const handleActionClick = (action: LedgerAgentAction) => {
        if (action.action_type === 'show_alternates') {
            if (!hasAlternates) return;
            setShowAlternates((prev) => !prev);
            return;
        }
        onAction(action.action_type, action.payload);
    };

    return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
            <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 text-amber-700" />
                <div>
                    <div className="text-[13px] font-medium text-amber-900">{checkpoint.prompt}</div>
                    {showRecommendedQuestion && (
                        <div className="mt-1 text-[11px] text-amber-800">{recommendedQuestion}</div>
                    )}
                </div>
            </div>

            {topCandidate && (
                <div className="rounded-lg border border-white/70 bg-white px-3 py-3">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="text-[13px] font-semibold text-gray-900">{topCandidate.vendor_name || 'Top candidate'}</div>
                            <div className="text-[11px] text-gray-500">Score {(topCandidate.score || 0).toFixed(3)}</div>
                        </div>
                        {topCandidate.finz_data?.fiscal_bucket && (
                            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] text-gray-600">
                                {topCandidate.finz_data.fiscal_bucket}
                            </span>
                        )}
                    </div>
                    {topCandidate.summary && (
                        <div className="mt-2 text-[13px] text-gray-700">{topCandidate.summary}</div>
                    )}
                </div>
            )}

            {conflicts.length > 0 && (
                <div className="space-y-1">
                    {conflicts.map((conflict, index) => (
                        <div key={`${conflict}-${index}`} className="text-[11px] text-amber-900">
                            {conflict}
                        </div>
                    ))}
                </div>
            )}

            {businessMemory?.preferred_mapping && (
                <div className="space-y-2">
                    {businessMemory?.preferred_mapping && (
                        <div className="text-[11px] text-amber-900">
                            Prior business memory: {businessMemory.vendor_name || 'vendor'}{businessMemory.preferred_mapping.fiscal_bucket ? ` -> ${businessMemory.preferred_mapping.fiscal_bucket}` : ''}{businessMemory.preferred_mapping.account_name ? ` / ${businessMemory.preferred_mapping.account_name}` : ''}
                        </div>
                    )}
                </div>
            )}

            {previewRows.length > 0 && (
                <div className="space-y-2">
                    <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-amber-700">
                        Affected Transactions
                    </div>
                    {previewRows.slice(0, 5).map((row, index) => {
                        const label = row.vendor_name || row.normalized_description || 'Transaction';
                        const mapping = [row.fiscal_bucket, row.finz_account_name].filter(Boolean).join(' / ');
                        return (
                            <div key={`${row.transaction_id || label}-${index}`} className="rounded-lg border border-white/70 bg-white px-3 py-2">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="truncate text-[13px] font-medium text-gray-900">{label}</div>
                                        <div className="mt-0.5 text-[11px] text-gray-500">
                                            {row.date || 'Unknown date'}{row.transaction_id ? ` - ${row.transaction_id}` : ''}
                                        </div>
                                    </div>
                                    <div className="shrink-0 text-[12px] font-medium text-gray-700">
                                        {typeof row.amount_abs === 'number' ? `$${row.amount_abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
                                    </div>
                                </div>
                                <div className="mt-1 text-[11px] text-gray-600">
                                    {row.resolution_status || 'unknown status'}{mapping ? ` - ${mapping}` : ''}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {visibleCandidates.length > 0 && (
                <div className="space-y-2">
                    {visibleCandidates.map((candidate: LedgerAgentCandidate, index: number) => (
                        <div key={candidate.candidate_id} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-[13px] font-medium text-gray-900">{candidate.vendor_name || 'Candidate'}</div>
                                <div className="text-[11px] text-gray-500">{(candidate.score || 0).toFixed(3)}</div>
                            </div>
                            {candidate.summary && (
                                <div className="mt-1 text-[11px] text-gray-600">{candidate.summary}</div>
                            )}
                            {candidate.evidence && candidate.evidence.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {candidate.evidence.slice(0, 3).map((item) => (
                                        <span key={`${candidate.candidate_id}-${item.source_id}-${item.kind}`} className="rounded-full bg-gray-100 px-2 py-1 text-[11px] text-gray-600">
                                            {item.label}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {showAlternates && index > 0 && (
                                <div className="mt-3">
                                    <button
                                        type="button"
                                        onClick={() => onAction('use_candidate', { candidate_id: candidate.candidate_id, candidate_index: index + 1 })}
                                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-100"
                                    >
                                        Use this candidate
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {visibleActions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {visibleActions.map((action, index) => (
                        <button
                            key={`${action.action_type}-${index}`}
                            type="button"
                            onClick={() => handleActionClick(action)}
                            className={action.variant === 'primary'
                                ? 'rounded-md bg-gray-900 px-3 py-2 text-[13px] text-white hover:bg-gray-800'
                                : 'rounded-md border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-100'}
                        >
                            {action.action_type === 'show_alternates' && showAlternates ? 'Hide alternates' : action.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const LedgerComposer: React.FC<{
    agentModeActive: boolean;
    checkpointPending?: boolean;
    checkpoint?: LedgerAgentCheckpoint | null;
    menuOpen: boolean;
    setMenuOpen: (open: boolean) => void;
    onEnableAgentMode: () => void;
    onDisableAgentMode: () => void;
    onSendMessage: (message: string) => void;
    onCheckpointAction?: (actionType: string, payload?: Record<string, unknown>) => void;
}> = ({
    agentModeActive,
    checkpointPending = false,
    checkpoint = null,
    menuOpen,
    setMenuOpen,
    onEnableAgentMode,
    onDisableAgentMode,
    onSendMessage,
    onCheckpointAction,
}) => {
        const [input, setInput] = useState('');
        const textAreaRef = useRef<HTMLTextAreaElement>(null);
        const menuRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (!menuOpen) return undefined;
            const handlePointerDown = (event: MouseEvent) => {
                if (!menuRef.current?.contains(event.target as Node)) {
                    setMenuOpen(false);
                }
            };
            document.addEventListener('mousedown', handlePointerDown);
            return () => document.removeEventListener('mousedown', handlePointerDown);
        }, [menuOpen, setMenuOpen]);

        useEffect(() => {
            if (!textAreaRef.current) return;
            const maxHeight = 200;
            textAreaRef.current.style.height = 'auto';
            const newHeight = Math.min(textAreaRef.current.scrollHeight, maxHeight);
            textAreaRef.current.style.height = `${newHeight}px`;
            textAreaRef.current.style.overflowY = textAreaRef.current.scrollHeight > maxHeight ? 'auto' : 'hidden';
        }, [input]);

        const checkpointPayload = (checkpoint?.payload || {}) as Record<string, unknown>;
        const checkpointActions = Array.isArray(checkpointPayload.actions) ? checkpointPayload.actions as LedgerAgentAction[] : [];
        const visibleCheckpointActions = checkpointActions.filter((action) => action.action_type !== 'show_alternates');
        const isBulkOperationCheckpoint = checkpoint?.checkpoint_type === 'bulk_operation_confirmation';
        const choiceActions: LedgerAgentAction[] = checkpointPending
            ? (
                isBulkOperationCheckpoint
                    ? [
                        { label: 'Confirm', action_type: 'confirm_bulk_operation', variant: 'primary' },
                        { label: 'Keep unchanged', action_type: 'skip_item', variant: 'secondary' },
                    ]
                    : visibleCheckpointActions
            )
            : [];
        const checkpointQuestion = isBulkOperationCheckpoint
            ? 'Please confirm if you want me to apply this change.'
            : 'Please confirm how you want me to proceed.';

        const handleSubmit = (event: React.FormEvent | React.KeyboardEvent) => {
            event.preventDefault();
            const trimmed = input.trim();
            if (!trimmed) return;
            onSendMessage(trimmed);
            setInput('');
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSubmit(event);
            }
        };

        return (
            <form onSubmit={handleSubmit} className="space-y-2">
                {agentModeActive && (
                    <div className="flex items-center gap-2">
                        <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700">
                            <AISparkle size={12} />
                            <span>Agent mode</span>
                            <button
                                type="button"
                                onClick={onDisableAgentMode}
                                className="rounded-full p-0.5 text-indigo-500 hover:bg-indigo-100 hover:text-indigo-700"
                                aria-label="Disable agent mode"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    </div>
                )}

                {checkpointPending ? (
                    <div className="space-y-3">
                        <div className="px-1 text-[15px] font-medium text-gray-900">
                            {checkpointQuestion}
                        </div>
                        <div className="overflow-hidden rounded-[22px] border border-gray-200 bg-white shadow-sm">
                            {choiceActions.length > 0 && (
                                <div className="px-3 pt-3">
                                    <div className="overflow-hidden rounded-[18px] border border-gray-200 bg-stone-50/70">
                                        {choiceActions.map((action, index) => (
                                            <button
                                                key={`${action.action_type}-${index}`}
                                                type="button"
                                                onClick={() => onCheckpointAction?.(action.action_type, action.payload)}
                                                className={`flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-white ${index > 0 ? 'border-t border-gray-200' : ''}`}
                                            >
                                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-[12px] font-medium text-gray-500 shadow-sm">
                                                    {index + 1}
                                                </div>
                                                <div className="min-w-0 flex-1 text-[14px] text-gray-800">
                                                    {action.label}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="relative mt-2 border-t border-gray-200 px-4 py-3">
                                <textarea
                                    ref={textAreaRef}
                                    value={input}
                                    onChange={(event) => setInput(event.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Reply with a custom instruction"
                                    className="min-h-[24px] max-h-[200px] w-full resize-none overflow-y-auto border-none bg-transparent pr-12 text-zinc-700 outline-none"
                                    rows={1}
                                />

                                <div className="absolute bottom-4 right-4">
                                    <button
                                        type="submit"
                                        disabled={!input.trim()}
                                        className={`${input.trim() ? 'bg-gray-800' : 'bg-gray-300'} flex h-8 w-8 items-center justify-center rounded-full text-white transition-colors`}
                                    >
                                        <Send className="h-4.5 w-4.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="relative rounded-xl border border-gray-200 bg-stone-50 p-4 shadow-sm">
                        <div className="absolute left-3 top-3" ref={menuRef}>
                            <button
                                type="button"
                                onClick={() => setMenuOpen(!menuOpen)}
                                className="flex h-8 w-8 items-center justify-center text-gray-500 transition-colors hover:text-gray-800"
                                aria-label="Open mode menu"
                            >
                                <Plus className="h-4.5 w-4.5" />
                            </button>

                            {menuOpen && (
                                <div className="absolute bottom-9 left-0 z-20 w-52 rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onEnableAgentMode();
                                            setMenuOpen(false);
                                        }}
                                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100"
                                    >
                                        <AISparkle size={13} />
                                        <div>
                                            <div className="font-medium text-gray-900">Agent mode</div>
                                            <div className="text-xs text-gray-500">Route this chat to Ledger Agent</div>
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>

                        <textarea
                            ref={textAreaRef}
                            value={input}
                            onChange={(event) => setInput(event.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={agentModeActive ? 'Tell the agent what to do' : 'Type a message'}
                            className="min-h-[24px] max-h-[200px] w-full resize-none overflow-y-auto border-none bg-transparent pl-10 pr-12 text-zinc-700 outline-none"
                            rows={1}
                        />

                        <div className="absolute bottom-4 right-4">
                            <button
                                type="submit"
                                disabled={!input.trim()}
                                className={`${input.trim() ? 'bg-gray-800' : 'bg-gray-300'} flex h-8 w-8 items-center justify-center rounded-full text-white transition-colors`}
                            >
                                <Send className="h-4.5 w-4.5" />
                            </button>
                        </div>
                    </div>
                )}

                <div className="text-center text-[10px] font-normal leading-[100%] tracking-[0px] text-[#989DA3]">
                    AI CFO can make mistakes. Please double-check responses.
                </div>
            </form>
        );
    };

const LedgerChatPanel: React.FC<Props> = ({ businessId, onClose, onOpenTransaction, onApplyResultSet, onRefreshLedger, onOpenNormalizationEditor }) => {
    const queryClient = useQueryClient();
    const [agentModeActive, setAgentModeActive] = useState<boolean>(() => {
        const storedBoolean = sessionStorage.getItem(STORAGE_KEYS.agentModeActive);
        if (storedBoolean === 'true') return true;
        if (storedBoolean === 'false') return false;
        const legacyMode = sessionStorage.getItem(STORAGE_KEYS.mode);
        return legacyMode === 'ledger_agent';
    });
    const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isStandardAccountSelectorOpen, setIsStandardAccountSelectorOpen] = useState(false);
    const [agentSessionId, setAgentSessionId] = useState<string | null>(() => (
        readStoredId(scopedStorageKey(STORAGE_KEYS.sessionId, businessId))
    ));
    const [panelMessages, setPanelMessages] = useState<DisplayMessage[]>(() => {
        try {
            const panelRaw = sessionStorage.getItem(scopedStorageKey(STORAGE_KEYS.panelMessages, businessId));
            if (panelRaw) return JSON.parse(panelRaw) as DisplayMessage[];
            const generalRaw = sessionStorage.getItem(scopedStorageKey(STORAGE_KEYS.generalMessages, businessId));
            return generalRaw ? JSON.parse(generalRaw) as DisplayMessage[] : [];
        } catch {
            return [];
        }
    });
    const [isSubmittingAgent, setIsSubmittingAgent] = useState(false);
    const [transientAgentJob, setTransientAgentJob] = useState<LedgerAgentJob | null>(null);
    const [generalConversationId, setGeneralConversationId] = useState<string | null>(() => (
        readStoredId(scopedStorageKey(STORAGE_KEYS.generalConversationId, businessId))
    ));
    const [isSubmittingGeneral, setIsSubmittingGeneral] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const previousBusinessIdRef = useRef<string | undefined>(businessId);
    const transientAgentJobRef = useRef<LedgerAgentJob | null>(null);
    const [pendingResolvedCheckpointId, setPendingResolvedCheckpointId] = useState<string | null>(null);
    const validAgentSessionId = useMemo(() => {
        const normalized = String(agentSessionId || '').trim().toLowerCase();
        return normalized && !['null', 'undefined'].includes(normalized) ? String(agentSessionId).trim() : null;
    }, [agentSessionId]);
    const lastAppliedResultSetMessageIdRef = useRef<string | null>(null);
    const lastAppliedFocusSignatureRef = useRef<string | null>(null);
    const lastLedgerRefreshMessageIdRef = useRef<string | null>(null);
    const lastAppliedWorkItemSignatureRef = useRef<string | null>(null);
    const pendingLedgerFilterReapplyRef = useRef(false);

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.agentModeActive, String(agentModeActive));
        sessionStorage.setItem(STORAGE_KEYS.mode, agentModeActive ? 'ledger_agent' : 'general');
    }, [agentModeActive]);

    useEffect(() => {
        const staleAgentSessionId = sessionStorage.getItem(scopedStorageKey(STORAGE_KEYS.sessionId, businessId));
        if (staleAgentSessionId && ['null', 'undefined', ''].includes(staleAgentSessionId.trim().toLowerCase())) {
            sessionStorage.removeItem(scopedStorageKey(STORAGE_KEYS.sessionId, businessId));
        }
        const staleGeneralConversationId = sessionStorage.getItem(scopedStorageKey(STORAGE_KEYS.generalConversationId, businessId));
        if (staleGeneralConversationId && ['null', 'undefined', ''].includes(staleGeneralConversationId.trim().toLowerCase())) {
            sessionStorage.removeItem(scopedStorageKey(STORAGE_KEYS.generalConversationId, businessId));
        }
    }, [businessId]);

    useEffect(() => {
        if (previousBusinessIdRef.current === businessId) return;
        previousBusinessIdRef.current = businessId;
        lastAppliedResultSetMessageIdRef.current = null;
        lastAppliedFocusSignatureRef.current = null;
        lastLedgerRefreshMessageIdRef.current = null;
        lastAppliedWorkItemSignatureRef.current = null;
        setAgentSessionId(readStoredId(scopedStorageKey(STORAGE_KEYS.sessionId, businessId)));
        setGeneralConversationId(readStoredId(scopedStorageKey(STORAGE_KEYS.generalConversationId, businessId)));
        try {
            const panelRaw = sessionStorage.getItem(scopedStorageKey(STORAGE_KEYS.panelMessages, businessId));
            if (panelRaw) {
                setPanelMessages(JSON.parse(panelRaw) as DisplayMessage[]);
                return;
            }
            const generalRaw = sessionStorage.getItem(scopedStorageKey(STORAGE_KEYS.generalMessages, businessId));
            setPanelMessages(generalRaw ? JSON.parse(generalRaw) as DisplayMessage[] : []);
        } catch {
            setPanelMessages([]);
        }
    }, [businessId]);

    useEffect(() => {
        if (agentSessionId && !validAgentSessionId) {
            setAgentSessionId(null);
        }
    }, [agentSessionId, validAgentSessionId]);

    useEffect(() => {
        if (agentSessionId) {
            sessionStorage.setItem(scopedStorageKey(STORAGE_KEYS.sessionId, businessId), agentSessionId);
        } else {
            sessionStorage.removeItem(scopedStorageKey(STORAGE_KEYS.sessionId, businessId));
        }
    }, [agentSessionId, businessId]);

    useEffect(() => {
        if (generalConversationId) {
            sessionStorage.setItem(scopedStorageKey(STORAGE_KEYS.generalConversationId, businessId), generalConversationId);
        } else {
            sessionStorage.removeItem(scopedStorageKey(STORAGE_KEYS.generalConversationId, businessId));
        }
    }, [generalConversationId, businessId]);

    useEffect(() => {
        sessionStorage.setItem(scopedStorageKey(STORAGE_KEYS.panelMessages, businessId), JSON.stringify(panelMessages));
    }, [panelMessages, businessId]);

    const sessionQuery = useQuery({
        queryKey: ['ledger-agent-session', businessId, validAgentSessionId],
        queryFn: () => ledgerAgentApi.getSession(validAgentSessionId as string),
        enabled: !!validAgentSessionId,
        refetchInterval: (query) => {
            if (!agentModeActive || !validAgentSessionId) return false;
            return shouldPollLedgerAgentSession(query.state.data) ? 5000 : false;
        },
        refetchIntervalInBackground: false,
    });

    useEffect(() => {
        if (!agentModeActive || !validAgentSessionId) return undefined;
        return ledgerAgentApi.streamSession(validAgentSessionId, {
            onSnapshot: (payload) => {
                queryClient.setQueryData(
                    ['ledger-agent-session', businessId, validAgentSessionId],
                    payload,
                );
                const streamedActiveJobId = String(payload.session?.active_job_id || '').trim();
                const streamedJobs = Array.isArray(payload.jobs) ? payload.jobs : [];
                const streamedActiveJob = streamedJobs.find((job) => job._id === streamedActiveJobId) || streamedJobs[0] || null;
                if (!transientAgentJobRef.current) return;
                if (streamedActiveJob && (streamedActiveJob.progress_events || []).length > 0) {
                    setTransientAgentJob(null);
                    return;
                }
                if (!streamedActiveJob || !['queued', 'running'].includes(String(streamedActiveJob.status || '').toLowerCase())) {
                    setTransientAgentJob(null);
                }
            },
        });
    }, [agentModeActive, businessId, queryClient, validAgentSessionId]);

    const session: LedgerAgentSession | null = sessionQuery.data?.session || null;
    const checkpoint = sessionQuery.data?.checkpoint || null;
    const jobs = useMemo(() => sessionQuery.data?.jobs || [], [sessionQuery.data?.jobs]);
    const focusContextLabel = useMemo(
        () => (transientAgentJob ? null : buildFocusContextLabel(session)),
        [session, transientAgentJob],
    );
    const latestAgentAssistantMessage = useMemo<DisplayMessage | null>(() => {
        const sessionMessages = (session?.messages || [])
            .filter((message) => message.role === 'assistant')
            .slice(-1);
        const latestSessionMessage = sessionMessages[0] || null;
        if (!latestSessionMessage) return null;
        return {
            id: `agent-${latestSessionMessage.id}`,
            role: 'ai' as const,
            content: latestSessionMessage.text,
            meta: {
                ...(latestSessionMessage.meta || {}),
                source: 'ledger_agent',
            },
        };
    }, [session?.messages]);
    const latestAppliedAgentMessage = useMemo<DisplayMessage | null>(() => {
        const reversed = [...panelMessages].reverse();
        return reversed.find((message) => {
            if (message.role !== 'ai' || message.meta?.source !== 'ledger_agent') return false;
            const meta = (message.meta || {}) as Record<string, unknown>;
            const applyResult = meta.apply_result && typeof meta.apply_result === 'object'
                ? meta.apply_result as Record<string, unknown>
                : null;
            const updatedTransactions = Number(applyResult?.updated_transactions || 0);
            return Boolean(
                updatedTransactions > 0
                || meta.change_preview
                || ((meta.change_previews as unknown[]) || []).length > 0
                || meta.before_after
            );
        }) || null;
    }, [panelMessages]);
    const starterPrompts = useMemo(() => {
        const focus = session?.working_memory?.last_focus;
        const selectedTransaction = focus?.selected_transaction && typeof focus.selected_transaction === 'object'
            ? focus.selected_transaction as Record<string, unknown>
            : null;
        const focusedVendor = String(selectedTransaction?.vendor_name || '').trim();
        const latestResultSet = latestAgentAssistantMessage?.meta?.result_set as { summary_preview?: ResultPreviewRow[] } | undefined;
        const summaryPreview = latestResultSet?.summary_preview;
        const previewRows = Array.isArray(summaryPreview) ? summaryPreview : [];
        const unresolvedPreviewVendor = previewRows.find((row) => ['needs_review', 'awaiting_approval'].includes(String(row.resolution_status || '').trim().toLowerCase()))?.vendor_name
            || previewRows.find((row) => String(row.vendor_name || '').trim())?.vendor_name
            || '';
        const suggestedVendor = focusedVendor || String(unresolvedPreviewVendor || '').trim();

        if (suggestedVendor) {
            return [
                `let's start from ${suggestedVendor}`,
                `show me all ${suggestedVendor} transactions`,
                `verify the ${suggestedVendor}`,
                'what can you do?',
            ];
        }

        return [
            'show me all needs review transactions',
            'show me all awaiting approval transaction',
            'what should I fix first here?',
            'what can you do?',
        ];
    }, [latestAgentAssistantMessage?.meta, session?.working_memory?.last_focus]);
    const contextualJobId = useMemo(() => {
        const sessionActiveJobId = String(session?.active_job_id || '').trim();
        if (sessionActiveJobId) return sessionActiveJobId;
        const checkpointJobId = String(checkpoint?.job_id || '').trim();
        if (checkpointJobId) return checkpointJobId;
        const messageJobId = String(latestAgentAssistantMessage?.meta?.job_id || '').trim();
        if (messageJobId) return messageJobId;
        if (!latestAgentAssistantMessage) {
            return String(session?.active_job_id || '').trim();
        }
        return '';
    }, [checkpoint?.job_id, latestAgentAssistantMessage, session?.active_job_id]);
    const activeJob = useMemo(() => {
        if (!contextualJobId) return null;
        return jobs.find((job) => job._id === contextualJobId) || null;
    }, [contextualJobId, jobs]);
    const displayJob = transientAgentJob || activeJob;
    const visibleCheckpoint = useMemo(() => {
        if (transientAgentJob) return null;
        if (!checkpoint) return null;
        if (pendingResolvedCheckpointId && checkpoint._id === pendingResolvedCheckpointId) {
            return null;
        }
        return checkpoint;
    }, [checkpoint, pendingResolvedCheckpointId, transientAgentJob]);
    const currentAgentWorkItem = useMemo<Record<string, unknown> | null>(() => {
        const checkpointPayload = visibleCheckpoint?.payload && typeof visibleCheckpoint.payload === 'object'
            ? visibleCheckpoint.payload as Record<string, unknown>
            : null;
        const checkpointWorkItem = checkpointPayload?.work_item && typeof checkpointPayload.work_item === 'object'
            ? checkpointPayload.work_item as Record<string, unknown>
            : null;
        if (checkpointWorkItem) return checkpointWorkItem;
        const result = displayJob?.result && typeof displayJob.result === 'object'
            ? displayJob.result as Record<string, unknown>
            : null;
        const resultWorkItem = result?.current_work_item && typeof result.current_work_item === 'object'
            ? result.current_work_item as Record<string, unknown>
            : null;
        if (resultWorkItem) return resultWorkItem;
        const queueState = result?.queue_state && typeof result.queue_state === 'object'
            ? result.queue_state as Record<string, unknown>
            : null;
        const queueWorkItems = queueState?.work_items;
        const workItems = Array.isArray(queueWorkItems)
            ? queueWorkItems.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
            : [];
        const currentIndex = Number(queueState?.current_index || 0);
        const queuedWorkItem = workItems[currentIndex] || null;
        if (queuedWorkItem) return queuedWorkItem;
        const latestEvent = (displayJob?.progress_events || []).at(-1);
        const eventWorkItemId = String(latestEvent?.meta?.work_item_id || '').trim();
        if (eventWorkItemId) {
            return workItems.find((item) => String(item.work_item_id || '').trim() === eventWorkItemId) || null;
        }
        const latestAgentMeta = latestAgentAssistantMessage?.meta && typeof latestAgentAssistantMessage.meta === 'object'
            ? latestAgentAssistantMessage.meta as Record<string, unknown>
            : null;
        const latestMessageWorkItemId = String(latestAgentMeta?.work_item_id || '').trim();
        if (latestMessageWorkItemId) {
            const matchedWorkItem = workItems.find((item) => String(item.work_item_id || '').trim() === latestMessageWorkItemId);
            if (matchedWorkItem) return matchedWorkItem;
            return parseWorkItemIdFallback(latestMessageWorkItemId, latestAgentMeta);
        }
        return null;
    }, [displayJob?.progress_events, displayJob?.result, latestAgentAssistantMessage?.meta, visibleCheckpoint?.payload]);
    const currentWorkItemLabel = useMemo(
        () => buildCurrentWorkItemLabel(currentAgentWorkItem),
        [currentAgentWorkItem],
    );
    const isResumingCheckpoint = Boolean(
        pendingResolvedCheckpointId
        && !visibleCheckpoint
        && activeJob
        && !['completed', 'failed', 'paused', 'interrupted'].includes(String(activeJob.status || '').toLowerCase()),
    );
    const isBulkOperationCheckpoint = visibleCheckpoint?.checkpoint_type === 'bulk_operation_confirmation';
    const isGuidedRemediationActive = isGuidedRemediationJob(displayJob);
    const displayJobStatus = String(displayJob?.status || '').trim().toLowerCase();
    const shouldShowGlobalAgentStatus = Boolean(
        visibleCheckpoint
        || isResumingCheckpoint
        || (displayJob && ['queued', 'running', 'awaiting_user', 'awaiting_dispatch'].includes(displayJobStatus))
    );

    useEffect(() => {
        if (!pendingResolvedCheckpointId) return;
        if (checkpoint && checkpoint._id !== pendingResolvedCheckpointId) {
            setPendingResolvedCheckpointId(null);
            return;
        }
        if (!checkpoint && (!activeJob || ['completed', 'failed', 'paused', 'interrupted'].includes(String(activeJob.status || '').toLowerCase()))) {
            setPendingResolvedCheckpointId(null);
        }
    }, [checkpoint, activeJob, pendingResolvedCheckpointId]);

    useEffect(() => {
        if (!transientAgentJob) return;
        if (!activeJob || activeJob._id !== transientAgentJob._id) return;
        if ((activeJob.progress_events || []).length > 0 || !['queued', 'running'].includes(String(activeJob.status || '').toLowerCase())) {
            setTransientAgentJob(null);
        }
    }, [activeJob, transientAgentJob]);

    useEffect(() => {
        transientAgentJobRef.current = transientAgentJob;
    }, [transientAgentJob]);

    useEffect(() => {
        if (!session?.messages) return;
        setPanelMessages(
            session.messages.map((message) => ({
                id: `agent-${message.id}`,
                role: (message.role === 'user' ? 'human' : 'ai') as 'human' | 'ai',
                content: message.role === 'user'
                    ? formatStructuredStandardAccountMessage(message.text)
                    : message.text,
                suggested_questions: Array.isArray((message.meta || {}).suggested_questions)
                    ? ((message.meta || {}).suggested_questions as string[])
                    : undefined,
                meta: {
                    ...(message.meta || {}),
                    source: 'ledger_agent',
                },
            }))
        );
    }, [session?.messages]);

    useEffect(() => {
        if (!pendingLedgerFilterReapplyRef.current) return;
        if (!latestAgentAssistantMessage || !onApplyResultSet) return;
        if (currentAgentWorkItem) return;
        const resultSet = latestAgentAssistantMessage.meta?.result_set as { filters?: ResultFilterChip[]; query?: Record<string, unknown> } | undefined;
        const filters = (resultSet?.filters || []).filter((item) => item?.label && item?.value);
        if (filters.length === 0) return;
        if (lastAppliedResultSetMessageIdRef.current === latestAgentAssistantMessage.id) return;
        lastAppliedResultSetMessageIdRef.current = latestAgentAssistantMessage.id;
        const vendorQuery = resultSet?.query && typeof resultSet.query === 'object'
            ? (resultSet.query.vendor_name as Record<string, unknown> | undefined)
            : undefined;
        const strictVendorMatch = Boolean(
            vendorQuery
            && typeof vendorQuery.$regex === 'string'
            && vendorQuery.$regex.startsWith('^')
            && vendorQuery.$regex.endsWith('$')
        );
        onApplyResultSet({ filters, strictVendorMatch });
        pendingLedgerFilterReapplyRef.current = false;
    }, [currentAgentWorkItem, latestAgentAssistantMessage, onApplyResultSet]);

    useEffect(() => {
        if (!pendingLedgerFilterReapplyRef.current) return;
        if (!session || !onApplyResultSet) return;
        if (transientAgentJob) return;
        if (currentAgentWorkItem) return;
        const focus = session.working_memory?.last_focus;
        if (!focus || typeof focus !== 'object') return;
        const filters = Array.isArray(focus.filters)
            ? focus.filters.filter((item) => item?.label && item?.value)
            : [];
        if (filters.length === 0) return;
        const focusSignature = JSON.stringify({
            sessionId: session._id,
            updatedAt: focus.updated_at || null,
            filters,
            transactionIds: Array.isArray(focus.transaction_ids) ? focus.transaction_ids : [],
            documentIds: Array.isArray(focus.document_ids) ? focus.document_ids : [],
        });
        if (lastAppliedFocusSignatureRef.current === focusSignature) return;
        lastAppliedFocusSignatureRef.current = focusSignature;
        onApplyResultSet({ filters });
        pendingLedgerFilterReapplyRef.current = false;
    }, [currentAgentWorkItem, session, transientAgentJob, onApplyResultSet]);

    useEffect(() => {
        if (!latestAppliedAgentMessage) return;
        if (lastLedgerRefreshMessageIdRef.current === latestAppliedAgentMessage.id) return;
        lastLedgerRefreshMessageIdRef.current = latestAppliedAgentMessage.id;
        const resultSet = latestAppliedAgentMessage.meta?.result_set as { filters?: ResultFilterChip[] } | undefined;
        const filters = (resultSet?.filters || []).filter((item) => item?.label && item?.value);
        if (filters.length > 0 && onApplyResultSet) {
            onApplyResultSet({ filters });
        } else {
            onRefreshLedger?.();
        }
    }, [latestAppliedAgentMessage, onApplyResultSet, onRefreshLedger]);

    useEffect(() => {
        if (!currentAgentWorkItem || !onApplyResultSet) return;
        const workItemSignature = JSON.stringify({
            workItemId: String(currentAgentWorkItem.work_item_id || ''),
            vendorName: String(currentAgentWorkItem.vendor_name || ''),
            normalizedDescription: String(currentAgentWorkItem.normalized_description || ''),
            direction: String(currentAgentWorkItem.direction || ''),
        });
        if (lastAppliedWorkItemSignatureRef.current === workItemSignature) return;
        const payload = buildWorkItemResultSet(currentAgentWorkItem);
        if (!payload) return;
        lastAppliedWorkItemSignatureRef.current = workItemSignature;
        onApplyResultSet(payload);
    }, [currentAgentWorkItem, onApplyResultSet]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        const target = scrollRef.current;
        if (!container || !target) return;
        const frame = window.requestAnimationFrame(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'end' });
            container.scrollTop = container.scrollHeight;
        });
        return () => window.cancelAnimationFrame(frame);
    }, [
        panelMessages,
        visibleCheckpoint?._id,
        agentModeActive,
        displayJob?._id,
        displayJob?.status,
        displayJob?.progress_events?.length,
        focusContextLabel,
        isSubmittingAgent,
        isSubmittingGeneral,
    ]);

    const handleSendAgentMessage = async (message: string, options?: { reapplyLedgerFilter?: boolean }) => {
        if (!message.trim()) return;
        setPendingResolvedCheckpointId(null);
        pendingLedgerFilterReapplyRef.current = Boolean(options?.reapplyLedgerFilter);
        const optimisticJobId = `transient-${Date.now()}`;
        setTransientAgentJob({
            _id: optimisticJobId,
            session_id: validAgentSessionId || 'pending-session',
            business_id: businessId || '',
            status: 'running',
            spec: { transient: true },
            progress_events: [
                {
                    id: `${optimisticJobId}-starting`,
                    stage: 'starting',
                    message: 'Agent is interpreting your request.',
                },
            ],
        });
        const optimisticHuman: DisplayMessage = {
            id: `panel-agent-human-${Date.now()}`,
            role: 'human',
            content: formatStructuredStandardAccountMessage(message),
            meta: { source: 'ledger_agent' },
        };
        setPanelMessages((prev) => [...prev, optimisticHuman]);
        setIsSubmittingAgent(true);
        try {
            const payload = agentSessionId
                ? await ledgerAgentApi.sendMessage(agentSessionId, message, businessId)
                : await ledgerAgentApi.createSession(message, null, businessId);
            setAgentSessionId(payload.session._id);
            setTransientAgentJob({
                ...payload.job,
                spec: { ...(payload.job.spec || {}), transient: true },
                progress_events: (payload.job.progress_events && payload.job.progress_events.length > 0)
                    ? payload.job.progress_events
                    : [
                        {
                            id: `${payload.job._id}-starting`,
                            stage: 'starting',
                            message: 'Agent is interpreting your request.',
                        },
                    ],
            });
            queryClient.setQueryData(
                ['ledger-agent-session', businessId, payload.session._id],
                (previous: { session?: LedgerAgentSession; jobs?: LedgerAgentJob[]; checkpoint?: LedgerAgentCheckpoint | null } | undefined) => ({
                    session: payload.session,
                    jobs: payload.job ? [payload.job, ...((previous?.jobs || []).filter((job) => job._id !== payload.job._id))] : (previous?.jobs || []),
                    checkpoint: previous?.checkpoint || null,
                }),
            );
            await sessionQuery.refetch();
        } catch (error: any) {
            setPanelMessages((prev) => prev.filter((item) => item.id !== optimisticHuman.id));
            setTransientAgentJob(null);
            setPendingResolvedCheckpointId(null);
            pendingLedgerFilterReapplyRef.current = false;
            const detail = String(
                error?.response?.data?.detail
                || error?.message
                || 'Could not reach the ledger agent.'
            ).trim();
            toast.error(detail);
        } finally {
            setIsSubmittingAgent(false);
        }
    };

    const handlePauseAgent = async () => {
        if (!activeJob?._id) return;
        await ledgerAgentApi.interruptJob(activeJob._id, 'pause');
        await sessionQuery.refetch();
    };

    const handleContinueAgentRun = async (instruction?: string) => {
        if (!instruction) return;
        await handleSendAgentMessage(instruction, { reapplyLedgerFilter: true });
    };

    const handleNewAgentSession = () => {
        setAgentSessionId(null);
        setPanelMessages([]);
    };

    const handleCheckpointAction = async (actionType: string, payload?: Record<string, unknown>) => {
        if (checkpoint?._id) {
            setPendingResolvedCheckpointId(checkpoint._id);
        }
        if (actionType === 'confirm_top_candidate' || actionType === 'confirm_bulk_operation') {
            await handleSendAgentMessage('confirm');
            return;
        }
        if (actionType === 'send_clarification_reply') {
            const clarificationMessage = buildStructuredStandardAccountMessage(payload);
            if (clarificationMessage) {
                await handleSendAgentMessage(clarificationMessage);
            }
            return;
        }
        if (actionType === 'undo_last_batch') {
            await handleSendAgentMessage('undo last batch');
            return;
        }
        if (actionType === 'apply_last_result_verified') {
            await handleSendAgentMessage('verify these all');
            return;
        }
        if (actionType === 'apply_last_result_awaiting_approval') {
            await handleSendAgentMessage('move these all to awaiting approval');
            return;
        }
        if (actionType === 'apply_last_result_needs_review') {
            await handleSendAgentMessage('move these all to needs review');
            return;
        }
        if (actionType === 'use_candidate') {
            const candidateId = String(payload?.candidate_id || '').trim();
            const candidateIndex = Number(payload?.candidate_index || 0);
            if (candidateId) {
                await handleSendAgentMessage(`use candidate ${candidateId}`, { reapplyLedgerFilter: false });
                return;
            }
            if (candidateIndex > 0) {
                await handleSendAgentMessage(`use alternate ${candidateIndex}`, { reapplyLedgerFilter: false });
                return;
            }
        }
        if (actionType === 'candidate_verify') {
            await handleSendAgentMessage('verify');
            return;
        }
        if (actionType === 'candidate_awaiting_approval') {
            await handleSendAgentMessage('move to awaiting approval');
            return;
        }
        if (actionType === 'candidate_keep_needs_review') {
            await handleSendAgentMessage('keep in needs review');
            return;
        }
        if (actionType === 'continue_guided_remediation') {
            await handleSendAgentMessage('continue to the next item');
            return;
        }
        if (actionType === 'stop_guided_remediation') {
            await handleSendAgentMessage('stop here');
            return;
        }
        if (actionType === 'open_normalization_editor') {
            onOpenNormalizationEditor?.(payload as LedgerAgentOpenNormalizationEditorPayload);
            return;
        }
        if (actionType === 'open_standard_account_selector') {
            setIsStandardAccountSelectorOpen(true);
            return;
        }
        if (actionType === 'skip_item') {
            const checkpointType = String(checkpoint?.checkpoint_type || '').trim().toLowerCase();
            if (checkpointType === 'guided_continue_confirmation') {
                await handleSendAgentMessage('stop here');
                return;
            }
            await handleSendAgentMessage('keep in needs review');
            return;
        }
    };

    const handleNewGeneralChat = () => {
        setGeneralConversationId(null);
        setPanelMessages([]);
    };

    const handleSendGeneralMessage = async (message: string) => {
        if (!message.trim()) return;
        const aiMessageId = `general-ai-${Date.now()}`;
        setPanelMessages((prev) => [
            ...prev,
            { id: `general-human-${Date.now()}`, role: 'human' as const, content: message, meta: { source: 'general' } },
            { id: aiMessageId, role: 'ai' as const, content: '', activity: [], suggested_questions: [], meta: { source: 'general' } },
        ]);
        setIsSubmittingGeneral(true);

        try {
            let streamedText = '';
            const payload = await chatApi.askStream(
                message,
                generalConversationId,
                businessId,
                {
                    onStatus: (event) => {
                        setPanelMessages((prev) => {
                            const updated = [...prev];
                            const index = updated.findIndex((message) => message.id === aiMessageId);
                            if (index === -1) return prev;
                            const last = updated[index];
                            updated[index] = {
                                ...last,
                                role: 'ai',
                                content: streamedText,
                                activity: appendActivityStep(last?.activity || [], event),
                            };
                            return updated;
                        });
                    },
                    onToken: (delta) => {
                        streamedText += delta;
                        setPanelMessages((prev) => {
                            const updated = [...prev];
                            const index = updated.findIndex((message) => message.id === aiMessageId);
                            if (index === -1) return prev;
                            updated[index] = {
                                id: updated[index]?.id || aiMessageId,
                                role: 'ai',
                                content: streamedText,
                                activity: updated[index]?.activity || [],
                                meta: { source: 'general' },
                            };
                            return updated;
                        });
                    },
                },
            );
            if (payload.conversation_id) {
                setGeneralConversationId(payload.conversation_id);
            }
            const response = payload.response;
            const text = typeof response === 'string' ? response : response?.text || streamedText || '';
            const suggestedQuestions = typeof response === 'object' ? (response?.suggested_questions || []) : [];
            setPanelMessages((prev) => {
                const updated = [...prev];
                const index = updated.findIndex((message) => message.id === aiMessageId);
                if (index === -1) return prev;
                updated[index] = {
                    id: updated[index]?.id || aiMessageId,
                    role: 'ai',
                    content: text,
                    activity: finalizeActivitySteps(updated[index]?.activity || []),
                    suggested_questions: suggestedQuestions,
                    meta: { source: 'general' },
                };
                return updated;
            });
        } finally {
            setIsSubmittingGeneral(false);
        }
    };

    const messageJobMap = useMemo(() => {
        const byId = new Map<string, LedgerAgentJob | null>();
        panelMessages.forEach((message, index) => {
            if (message.role !== 'human' || message.meta?.source !== 'ledger_agent') return;

            const explicitJobId = String(message.meta?.job_id || '').trim();
            if (explicitJobId) {
                byId.set(message.id, jobs.find((job) => job._id === explicitJobId) || null);
                return;
            }

            for (let cursor = index + 1; cursor < panelMessages.length; cursor += 1) {
                const candidate = panelMessages[cursor];
                if (candidate.meta?.source !== 'ledger_agent') continue;
                if (candidate.role === 'human') break;
                const candidateJobId = String(candidate.meta?.job_id || '').trim();
                if (candidateJobId) {
                    byId.set(message.id, jobs.find((job) => job._id === candidateJobId) || null);
                    return;
                }
            }

            const isLatestLedgerHuman = [...panelMessages]
                .reverse()
                .find((candidate) => candidate.role === 'human' && candidate.meta?.source === 'ledger_agent')
                ?.id === message.id;
            if (isLatestLedgerHuman) {
                byId.set(message.id, displayJob || null);
            }
        });
        return byId;
    }, [displayJob, jobs, panelMessages]);

    return (
        <div
            className={`flex flex-col bg-white overflow-hidden ${isExpanded ? 'fixed inset-y-0 left-16 right-0 z-[1000]' : 'relative h-full'}`}
            style={{ height: isExpanded ? '100dvh' : '100%' }}
        >
            <div className="h-[48px] px-4 flex items-center justify-between bg-[#F1F2F4] border-b border-gray-100 flex-shrink-0 z-10">
                <div className="flex items-center gap-2">
                    <AISparkle size={17} />
                    <h2 className="text-sm font-bold text-slate-800">AI CFO</h2>
                </div>

                <div className="flex items-center gap-3 text-gray-500">
                    {!isExpanded ? (
                        <button onClick={() => setIsExpanded(true)} className="hover:text-slate-800 transition-colors" title="Expand">
                            <Expand size={18} />
                        </button>
                    ) : (
                        <button onClick={() => setIsExpanded(false)} className="hover:text-slate-800 transition-colors" title="Shrink">
                            <Minimize2 size={18} />
                        </button>
                    )}
                    <button
                        onClick={agentModeActive ? handleNewAgentSession : handleNewGeneralChat}
                        className="hover:text-slate-800 transition-colors"
                        title="New Chat"
                    >
                        <SquarePen size={18} />
                    </button>
                    <button className="hover:text-slate-800 transition-colors" title="Prompt Library" disabled>
                        <LibraryBig size={18} />
                    </button>
                    <button
                        onClick={() => {
                            setIsExpanded(false);
                            onClose?.();
                        }}
                        className="hover:text-slate-800 transition-colors"
                        title="Close"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 scroll-smooth text-black chat-scrollbar-invisible">
                <div className="w-full max-w-6xl mx-auto sm:px-2 pt-4">
                    {panelMessages.length === 0 && !isSubmittingGeneral && !isSubmittingAgent && (
                        <div className="flex flex-col gap-2 mt-6">
                            {agentModeActive && (
                                <p> Hi, I'm Ledger Agent. Ask me anything about your transactions. Start with a query, point to something that looks wrong, or ask what to do next.</p>
                            )}
                            {(agentModeActive
                                ? starterPrompts
                                : ['What changed in my cash flow?', 'Summarize my latest business performance.', 'What should I pay attention to this week?']
                            ).map((prompt) => (
                                <button
                                    key={prompt}
                                    onClick={() => (agentModeActive ? handleSendAgentMessage(prompt, { reapplyLedgerFilter: true }) : handleSendGeneralMessage(prompt))}
                                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition text-left"
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div>
                    )}

                    {panelMessages.map((message) => (
                        <div key={message.id} className="py-4">
                            <MessageBubble
                                message={message}
                                onSuggestionClick={(question) => (agentModeActive ? handleSendAgentMessage(question, { reapplyLedgerFilter: true }) : handleSendGeneralMessage(question))}
                                onActionClick={handleCheckpointAction}
                                onOpenTransaction={onOpenTransaction}
                            />
                            {message.role === 'human' && message.meta?.source === 'ledger_agent' && (() => {
                                const messageJob = messageJobMap.get(message.id) || null;
                                if (!messageJob) return null;

                                return (
                                    <div className="mt-4 space-y-3">
                                        <ActivityCard
                                            job={messageJob}
                                            diagnosis={(message.meta?.diagnosis as LedgerDiagnosisSummary | undefined) || null}
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    ))}

                    {(isSubmittingGeneral || isSubmittingAgent) && (
                        <div className="py-3">
                            <LoadingDots />
                        </div>
                    )}
                    {agentModeActive && ((!visibleCheckpoint && shouldShowGlobalAgentStatus) || currentWorkItemLabel || focusContextLabel) && (
                        <div className="pb-4 space-y-3">
                            {/* {!visibleCheckpoint && shouldShowGlobalAgentStatus && (
                                <AgentStatusBar
                                    job={displayJob}
                                    resumingCheckpoint={isResumingCheckpoint}
                                    onStop={handlePauseAgent}
                                />
                            )} */}
                            {(currentWorkItemLabel || focusContextLabel) && (
                                <div className="flex items-center gap-2">
                                    <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-[11px] text-gray-600">
                                        <AISparkle size={11} />
                                        <span className="truncate">{currentWorkItemLabel || focusContextLabel}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div ref={scrollRef} />
                </div>
            </div>

            <div className="w-full bg-white border-t border-gray-100 p-4 pb-6 flex-shrink-0 z-10">
                <div className="w-full max-w-6xl mx-auto space-y-3">
                    <LedgerComposer
                        agentModeActive={agentModeActive}
                        checkpointPending={Boolean(visibleCheckpoint)}
                        checkpoint={visibleCheckpoint}
                        menuOpen={isModeMenuOpen}
                        setMenuOpen={setIsModeMenuOpen}
                        onEnableAgentMode={() => setAgentModeActive(true)}
                        onDisableAgentMode={() => setAgentModeActive(false)}
                        onSendMessage={agentModeActive ? ((message) => void handleSendAgentMessage(message, { reapplyLedgerFilter: true })) : handleSendGeneralMessage}
                        onCheckpointAction={handleCheckpointAction}
                    />
                </div>
            </div>
            <StandardAccountSelector
                isOpen={isStandardAccountSelectorOpen}
                onClose={() => setIsStandardAccountSelectorOpen(false)}
                currentAccountId={null}
                businessId={businessId}
                onSelect={(account: StandardAccount) => {
                    setIsStandardAccountSelectorOpen(false);
                    void handleSendAgentMessage(
                        `__structured_standard_account__:${String(account._id || '').trim()}|${String(account.fiscal_bucket || '').trim()}|${String(account.display_name || account.account_name || '').trim()}|${String(account.account_number || '').trim()}`,
                        { reapplyLedgerFilter: false },
                    );
                }}
            />
        </div>
    );
};

export default LedgerChatPanel;
